import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { langOverrideState, shopDetailState } from 'store/atoms';
import { SupportedLanguage, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, COUNTRY_TO_LANGUAGE } from './types';

import ko from './locales/ko.json';
import en from './locales/en.json';
import ja from './locales/ja.json';
import zhCN from './locales/zh-CN.json';
import zhTW from './locales/zh-TW.json';
import th from './locales/th.json';

type TranslationData = Record<string, unknown>;

const translations: Record<SupportedLanguage, TranslationData> = {
  ko,
  en,
  ja,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  th,
};

interface I18nContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextType | null>(null);

function getNestedValue(obj: TranslationData, key: string): string | undefined {
  const keys = key.split('.');
  let current: unknown = obj;

  for (const k of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[k];
  }

  return typeof current === 'string' ? current : undefined;
}

function getLanguageFromCountryCode(countryCode: string | undefined): SupportedLanguage {
  if (!countryCode) return DEFAULT_LANGUAGE;
  return COUNTRY_TO_LANGUAGE[countryCode] || DEFAULT_LANGUAGE;
}

interface I18nProviderProps {
  children: React.ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const shopDetail = useRecoilValue(shopDetailState);
  const countryCode = shopDetail?.solutionConfig?.global_config?.countryCode;
  const langOverride = useRecoilValue(langOverrideState);

  const [language, setLanguageState] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
      document.documentElement.lang = lang;
    }
  }, []);

  useEffect(() => {
    // URL ?lang= 파라미터가 있으면 매장 설정 언어(countryCode)보다 우선
    const newLanguage = langOverride ?? getLanguageFromCountryCode(countryCode);
    setLanguageState(newLanguage);
    document.documentElement.lang = newLanguage;
  }, [countryCode, langOverride]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      let value = getNestedValue(translations[language], key);

      // 현재 언어 값이 비어있으면 영어 폴백 시도 (스프레드시트의 빈 셀 대응).
      // 단, 영어 값이 의도적으로 빈 문자열인 경우(예: 한국어 어순에만 필요한 suffix)는
      // 그 빈 문자열을 그대로 채택해 키가 노출되지 않도록 한다.
      if ((value === undefined || value === '') && language !== DEFAULT_LANGUAGE) {
        const fallback = getNestedValue(translations[DEFAULT_LANGUAGE], key);
        if (fallback !== undefined) value = fallback;
      }

      // 키 자체가 어디에도 정의되지 않은 경우에만 키를 그대로 노출한다.
      if (value === undefined) {
        console.warn(`Translation missing: ${key}`);
        return key;
      }

      if (params) {
        return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => {
          return params[paramKey]?.toString() ?? `{{${paramKey}}}`;
        });
      }

      return value;
    },
    [language]
  );

  return <I18nContext.Provider value={{ language, setLanguage, t }}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

export function useTranslation() {
  const { t, language } = useI18n();
  return { t, language };
}

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './types';
export type { SupportedLanguage } from './types';
